import { observer } from 'mobx-react-lite'
import { Typography } from '@mui/material'
import { AppPaper } from 'src/component/common/analog/molecule/AppPaper'
import { AppButton } from 'src/component/common/analog/molecule/button/AppButton'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { ColorTokens } from 'src/feature/theme/model/Palette'

/**
 * Props for CategorySelectionCard component
 */
interface CategorySelectionCardProps {
  categoryId: string
  categoryName: string
  categoryDescription: string
  skillCount: number
  isAllSkillsRated: boolean
  onRateClick: (categoryId: string) => void
}

/**
 * Compact card for category selection in multi-category view
 * Shows category name, description, and a rate/update button
 */
export const CategorySelectionCard = observer(({
  categoryId,
  categoryName,
  categoryDescription,
  skillCount,
  isAllSkillsRated,
  onRateClick,
}: CategorySelectionCardProps) => {
  const { translation } = useLocalizationStore()
  const translations = translation.pages.myTeam.rolesButton

  return (
    <AppPaper sx={styles.root}>
      <AppBox sx={styles.content}>
        <Typography variant='h3' sx={styles.heading}>
          {categoryName}
        </Typography>
        <Typography sx={styles.description}>
          {categoryDescription}
        </Typography>
        <Typography sx={styles.skillCount}>
          {skillCount} {translation.pages.myTeam.setDemands().toLowerCase()}
        </Typography>
      </AppBox>
      <AppButton
        fontSize={12}
        variant='contained'
        onClick={() => onRateClick(categoryId)}
      >
        {isAllSkillsRated ? translations.updateSkill() : translations.rateSkill()}
      </AppButton>
    </AppPaper>
  )
})

const styles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 20,
    width: '100%',
    maxWidth: 320,
    padding: '10px 15px',
    borderRadius: '12px',
    minHeight: 200,
    border: '1px solid',
    borderColor: 'transparent',
    transition: 'border-color 0.2s ease-in-out',
    '&:hover': {
      borderColor: ColorTokens.border100,
    },
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 10,
    flex: 1,
    width: '100%',
  },
  heading: {
    color: 'text.primary',
    display: '-webkit-box',
    WebkitLineClamp: 1,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  description: {
    fontSize: 12,
    color: 'text.secondary',
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  skillCount: {
    fontSize: 11,
    color: 'secondary.main',
    fontWeight: 600,
  },
}
